import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Box, 
  Button, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Snackbar, 
  Alert,
  CircularProgress,
  Grid2
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import UsersTable from "./UsersTable";
import UserService from "../../services/UserService";

const ManageUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [userService] = useState(() => new UserService());

  const fetchUsers = async () => {
    console.log('ManageUsers: Starting to fetch users...');
    setLoading(true);
    try {
      console.log('ManageUsers: Calling userService.listUsers...');
      const result = await userService.listUsers(100); // Get up to 100 users
      console.log('ManageUsers: Raw result from API:', result);
      
      const transformedUsers = userService.transformUsers(result.Users || []);
      console.log('ManageUsers: Transformed users:', transformedUsers);
      
      setUsers(transformedUsers);
    } catch (error) {
      console.error("ManageUsers: Error fetching users:", error);
      setSnackbarMessage("Error fetching users. Make sure you have admin privileges and the Admin Queries API is configured.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      console.log('ManageUsers: Starting to load users...');
      setLoading(true);
      try {
        console.log('ManageUsers: Calling userService.listUsers...');
        const result = await userService.listUsers(100);
        console.log('ManageUsers: Raw result from API:', result);
        
        if (result && result.Users) {
          const transformedUsers = userService.transformUsers(result.Users);
          console.log('ManageUsers: Transformed users:', transformedUsers);
          setUsers(transformedUsers);
        } else {
          console.log('ManageUsers: No users in result or malformed response:', result);
          setUsers([]);
        }
      } catch (error) {
        console.error("ManageUsers: Error fetching users:", error);
        setSnackbarMessage(`Error fetching users: ${error.message}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [userService]);

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSnackbarMessage(location.state.successMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // Clear the state to prevent the message from showing again
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleBackClick = () => {
    navigate("/settings");
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setOpen(true);
  };

  const handleEditUser = (user) => {
    // TODO: Implement user editing functionality
    setSnackbarMessage("User editing functionality will be implemented soon.");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  const handleToggleUserStatus = async (user) => {
    try {
      if (user.enabled) {
        await userService.disableUser(user.username);
        setSnackbarMessage(`User ${user.fullName} has been disabled.`);
      } else {
        await userService.enableUser(user.username);
        setSnackbarMessage(`User ${user.fullName} has been enabled.`);
      }
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // Refresh the user list
      fetchUsers();
    } catch (error) {
      console.error("Error toggling user status:", error);
      setSnackbarMessage("Error updating user status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      // TODO: Implement user deletion functionality
      setSnackbarMessage("User deletion functionality will be implemented soon.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      setOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarMessage("Error deleting user.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setUserToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid2>
          <IconButton 
            onClick={handleBackClick}
            aria-label="back to settings"
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid2>
        <Grid2 xs>
          <Typography variant="h2" gutterBottom sx={{ mb: 0 }}>
            Manage Users
          </Typography>
        </Grid2>
        <Grid2>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
        </Grid2>
        <Grid2>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSnackbarMessage("Add user functionality will be implemented soon.");
              setSnackbarSeverity("info");
              setSnackbarOpen(true);
            }}
          >
            Add User
          </Button>
        </Grid2>
      </Grid2>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <UsersTable
          users={users}
          onDeleteUser={handleDeleteUser}
          onEditUser={handleEditUser}
          onToggleUserStatus={handleToggleUserStatus}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user "{userToDelete?.fullName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUsers;
